import React, { useEffect, useState } from 'react';
import { Box, Link } from '@mui/material';
import { leadApi, esignApi, fileApi } from '../../services';
import { StatusPill, DetailRow } from './DetailRow';

export interface KycDetailsValue {
  leadId?: string;
  kyc?: boolean;
}

export interface EsignDetailsValue {
  leadId?: string;
  verificationId?: string;
  docId?: string;
  esign?: boolean;
}

export const isKycValue = (value: any): value is KycDetailsValue =>
  value && typeof value === 'object' && value.kyc === true && !!value.leadId;

export const isEsignValue = (value: any): value is EsignDetailsValue =>
  value &&
  typeof value === 'object' &&
  value.esign === true &&
  !!value.leadId &&
  !!value.verificationId;

interface KraEntry {
  kraName?: string;
  status?: string;
  isKycVerified?: boolean;
  kycStatusDate?: string;
  createdAt?: string;
  documents?: { privateUrl?: string }[];
}

const latestByCreatedAt = <T extends { createdAt?: string }>(
  items: T[],
): T | undefined =>
  items.length
    ? [...items].sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime(),
      )[0]
    : undefined;

const formatDate = (value: string) =>
  isNaN(Date.parse(value))
    ? value
    : new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

const KycDetails: React.FC<{ tenantId?: string; leadId?: string }> = ({
  tenantId,
  leadId,
}) => {
  const [lead, setLead] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!tenantId || !leadId) return;
    setLoading(true);
    setError(false);
    leadApi
      .getLead(tenantId, leadId)
      .then((result) => setLead(result?.data || result))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tenantId, leadId]);

  if (loading) {
    return <StatusPill label="Loading status" tone="neutral" loading />;
  }
  if (error || !lead) {
    return <StatusPill label="Unable to fetch lead" tone="warning" />;
  }

  const kraEntries: KraEntry[] = Array.isArray(lead.kra) ? lead.kra : [];
  const entry = latestByCreatedAt(kraEntries);
  const verified = !!entry?.isKycVerified;
  const docUrl = entry?.documents?.[0]?.privateUrl;

  return (
    <Box>
      <StatusPill
        label={verified ? 'KYC Verified' : entry?.status || 'Not Verified'}
        tone={verified ? 'success' : 'warning'}
      />
      <DetailRow label="Lead ID:">{leadId || '—'}</DetailRow>
      {entry?.kraName && <DetailRow label="KRA:">{entry.kraName}</DetailRow>}
      {entry?.kycStatusDate && (
        <DetailRow label="Verified on:">{entry.kycStatusDate}</DetailRow>
      )}
      {docUrl && <DetailRow label="Document:">{docUrl}</DetailRow>}
    </Box>
  );
};

const EsignDetails: React.FC<{
  tenantId?: string;
  leadId?: string;
  verificationId?: string;
  docId?: string;
}> = ({ tenantId, leadId, verificationId, docId }) => {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [docDownloadUrl, setDocDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !verificationId) return;
    setLoading(true);
    setError(false);
    esignApi
      .getStatus(tenantId, verificationId)
      .then((result) => setStatus(result?.data || result))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tenantId, verificationId]);

  useEffect(() => {
    const privateUrl = status?.signedDocCrmPrivateUrl;
    if (!tenantId || !privateUrl) {
      setDocDownloadUrl(null);
      return;
    }
    fileApi
      .getDownloadUrl(tenantId, privateUrl)
      .then((result) =>
        setDocDownloadUrl(
          typeof result === 'string'
            ? result
            : result?.data?.url || result?.url || null,
        ),
      )
      .catch(() => setDocDownloadUrl(null));
  }, [tenantId, status?.signedDocCrmPrivateUrl]);

  if (loading) {
    return <StatusPill label="Loading status" tone="neutral" loading />;
  }
  if (error || !status) {
    return <StatusPill label="Unable to fetch eSign status" tone="warning" />;
  }

  const rawStatus = String(status?.status || '').toUpperCase();
  const signedAt =
    status?.signedAt || status?.signed_at || status?.completedAt;

  return (
    <Box>
      <StatusPill
        label={rawStatus || 'Unknown'}
        tone={['SUCCESS', 'COMPLETED'].includes(rawStatus) ? 'success' : 'warning'}
      />
      <DetailRow label="Lead ID:">{leadId || '—'}</DetailRow>
      <DetailRow label="Verification ID:">{verificationId || '—'}</DetailRow>
      {docId && <DetailRow label="Doc ID:">{docId}</DetailRow>}
      {signedAt && <DetailRow label="Signed on:">{formatDate(String(signedAt))}</DetailRow>}
      {docDownloadUrl && (
        <DetailRow label="Document:">
          <Link href={docDownloadUrl} target="_blank" rel="noopener noreferrer" underline="hover">
            View Signed Document
          </Link>
        </DetailRow>
      )}
    </Box>
  );
};

interface LeadVerificationDetailsProps {
  tenantId?: string;
  type: 'kyc' | 'esign';
  leadId?: string;
  verificationId?: string;
  docId?: string;
}

export const LeadVerificationDetails: React.FC<
  LeadVerificationDetailsProps
> = ({ tenantId, type, leadId, verificationId, docId }) =>
  type === 'kyc' ? (
    <KycDetails tenantId={tenantId} leadId={leadId} />
  ) : (
    <EsignDetails
      tenantId={tenantId}
      leadId={leadId}
      verificationId={verificationId}
      docId={docId}
    />
  );
